/** Help — how the product treats your data, how to read what it shows, and how to reach us.
 *
 * Gate 31. This was a placeholder that said it was one (*"There is no help centre in this product
 * yet"*), which was the right sentence while it was true. It is replaced by three tabs on the one
 * existing route:
 *
 * - **How your data is processed** — what customers were promised at onboarding. The words are in
 *   `helpContent.ts` as data, so this page and the shell's search read the same sentences.
 * - **Reading the screens** — what a receipt is, and the difference between *nothing here*, *not
 *   assessed* and *could not be loaded*, which is the distinction gate 32 made consistent.
 * - **Contact** — an address if one has been decided (`contact.ts`) and an honest sentence if not.
 *   There is **no case system**: P-08 moved support to a separate internal application.
 *
 * **Tabs are the shared tablist** (`RecordTabs`), not a second one: forty lines of ARIA with roving
 * `tabIndex` and a live region that exists from the first render. The tab is in the URL, so
 * `/help?tab=contact` is the contact route — it needed no change to the router, which belongs to
 * the orchestrator, and a link to it can be pasted into an email.
 *
 * Help is **not role-gated**: a Viewer, a Contributor and an Auditor all need to know where their
 * data goes. `RequireSession` is still the frame, because the shell needs a session to be drawn.
 *
 * **Redesign (2026-10-30).** The tablist sits in the page header; each section is a card with an
 * "On this page" list beside it, so a long topic is a thing you can move around in instead of one
 * scroll. **No sentence moved or changed**: the first section of the data tab is the page's own
 * statement that it is not a legal notice, and it stays first and in view, not folded away.
 */

import { Question } from "@phosphor-icons/react";

import { PageHeaderCard } from "../components/ui";
import { RecordPanel, RecordTabs } from "../screens/RecordTabs";
import { ScreenFrame } from "../screens/ScreenFrame";
import { useScreen } from "../screens/useScreen";
import styles from "./Help.module.css";
import { useSupportEmail } from "./contact";
import {
  CONTACT_KEY,
  DEFAULT_HELP_TAB,
  HELP_TABS,
  TOPICS,
  type HelpSection,
  type HelpTopic,
} from "./helpContent";
import { RequireSession } from "./RequireSession";

/** What the contact tab is about, for the line under the tabs. */
const CONTACT_ABOUT = "who to write to, and what that does and does not cover";

/** The reader's way to us — an address if one is published, a plain statement if not. */
/** The contact panel with the deployment's configured address (`GET /api/v1/config`). */
function ConfiguredContactPanel() {
  return <ContactPanel address={useSupportEmail()} />;
}

export function ContactPanel({ address }: { address: string | null }) {
  return (
    <div className={styles.card} data-testid="help-contact-panel">
      <h2 className={styles.heading}>Contact</h2>
      {address ? (
        <p className={styles.text} data-testid="help-contact" data-published="yes">
          Write to <a href={`mailto:${address}`}>{address}</a>.
        </p>
      ) : (
        <p className={styles.text} data-testid="help-contact" data-published="no">
          No support address has been published in the product yet. Until one is, use the contact
          you were given when your workspace was set up.
        </p>
      )}
      <p className={styles.text}>
        This is a contact, not a support desk. There is no ticket, queue or response time in the
        product, and no promise of one is implied.
      </p>
      <p className={styles.text}>
        When you write, say which screen you were on and what it said, and include the address bar.
        Do not send personal data or the contents of a document by email: the receipt on a figure
        already names the document it came from, and that is enough for us to find it.
      </p>
      <p className={styles.text}>
        You can record a request about your own data in{" "}
        <a href="/privacy-requests">Your data requests</a>. It records a receipt only: it does not
        verify your identity, does not route it for handling, and does not start a response clock.
        For a question about your data or to ask for handling of a request, use the same contact
        above. Recording a request does not erase a person or show that it has been handled.
      </p>
    </div>
  );
}

function Section({ section }: { section: HelpSection }) {
  return (
    <section
      id={`help-${section.id}`}
      className={styles.card}
      data-testid={`help-section-${section.id}`}
    >
      <h2 className={styles.heading}>{section.heading}</h2>
      {section.list ? (
        <ul className={styles.list}>
          {section.body.map((line) => (
            <li key={line} className={styles.text}>
              {line}
            </li>
          ))}
        </ul>
      ) : (
        section.body.map((line) => (
          <p key={line} className={styles.text}>
            {line}
          </p>
        ))
      )}
    </section>
  );
}

/** The topic's sections, with a list of them beside. Each entry scrolls to its card; it is a
 * button, not an anchor, because a `#fragment` would go through the router and the page's own
 * scroll box is not the window. */
function TopicBody({ topic }: { topic: HelpTopic }) {
  return (
    <div className={styles.topic}>
      <nav className={styles.toc} aria-label="On this page">
        <span className={styles.tocTitle}>On this page</span>
        <ul>
          {topic.sections.map((section) => (
            <li key={section.id}>
              <button
                type="button"
                className={styles.tocLink}
                onClick={() =>
                  document
                    .getElementById(`help-${section.id}`)
                    ?.scrollIntoView?.({ behavior: "smooth", block: "start" })
                }
              >
                {section.heading}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className={styles.sections}>
        {topic.sections.map((section) => (
          <Section key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}

function HelpBody() {
  const screen = useScreen();
  // A tab the URL names that nothing matches falls back to the first: a stale link should land on
  // help, not on a blank panel under a tablist with nothing selected.
  const tab = HELP_TABS.some((entry) => entry.key === screen.tab) ? (screen.tab as string) : DEFAULT_HELP_TAB;
  const topic = TOPICS.find((entry) => entry.key === tab);
  const label = HELP_TABS.find((entry) => entry.key === tab)?.label ?? tab;
  const about = topic ? topic.about : CONTACT_ABOUT;

  return (
    <ScreenFrame screen={screen} title="Help" crumb="your data, the screens, and how to reach us" bodyScrolls>
      <PageHeaderCard icon={Question} title="Help" label={`${about.charAt(0).toUpperCase()}${about.slice(1)}.`} />
      <div className={styles.body}>
        {/* The tabs sit on the left under the header card, like every other place's. */}
        <RecordTabs
          tabs={HELP_TABS}
          tab={tab}
          onSelect={screen.setTab}
          label="Help"
          saying={<>Showing {label}.</>}
        />
        <RecordPanel tab={tab}>
          {topic ? (
            <TopicBody topic={topic} />
          ) : tab === CONTACT_KEY ? (
            <ConfiguredContactPanel />
          ) : null}
        </RecordPanel>
      </div>
    </ScreenFrame>
  );
}

export function HelpScreen() {
  return (
    <RequireSession>
      <HelpBody />
    </RequireSession>
  );
}
